<script>
import axios from 'axios';
import DataTable from 'datatables.net-vue3';
import moment from 'moment';
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import Swal from 'sweetalert2';

export default {
    name: "MdmMainTable",
    components: {
        DataTable,
        flatPickr
    },
    props: {
        canExport: { type: Boolean, default: false }
    },
    data() {
        return {
            deviceList: [],
            loading: true,
            status: '',
            mdmStatus: '',
            os: '',
            dtKey: 0,
            isButtonDisabled: false,
            messageBag: [],
            
            // Export Feature States
            showExportModal: false,
            exportFilters: {
                dateRange: '',
                types: [1, 2, 3], // 1: Warning, 2: Enable Lost Mode, 3: Disable Lost Mode
                format: 'excel'
            },
            flatpickrConfig: {
                mode: 'range',
                dateFormat: 'Y-m-d',
            },

            // History Modal States
            showHistoryModal: false,
            historyLoading: false,
            historyList: [],
            selectedDeviceName: '',
            selectedDevice: null,

            options: {
                searching: true,
                ordering: false,
                responsive: true
            },
        };
    },
    methods: {
        format_date(value) {
            if (value) {
                return moment(String(value)).format('DD-MM-YYYY HH:mm:ss')
            }
            return '---';
        },
        getDeviceList() {
            this.loading = true;
            axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt')
            
            const params = {
                paginate: 0,
                enrollment_status: this.status,
                status: this.mdmStatus,
                os: this.os
            };

            axios.get(process.env.VUE_APP_API_BASEURL + 'mdm', { params })
                .then(response => {
                    if (response && response.data && response.data.data) {
                        this.loading = false;
                        
                        this.deviceList = response.data.data;

                        this.dtKey += 1;
                    } else {
                        this.loading = false;
                        this.deviceList = [];
                        this.messageBag = ['No record found.'];
                    }
                })
                .catch(error => {
                    this.loading = false;
                    this.messageBag = [error.response ? error.response.data.message : error.message];
                });
        },
        submitFilter(e) {
            e.preventDefault();
            this.isButtonDisabled = true;
            this.getDeviceList();
            this.isButtonDisabled = false;
        },
        resetFilter() {
            this.status = '';
            this.mdmStatus = '';
            this.os = '';
            this.getDeviceList();
        },

        // Export Modal Methods
        openExportModal() {
            this.showExportModal = true;
        },
        closeExportModal() {
            this.showExportModal = false;
        },
        handleExportDevices() {
            if (!this.exportFilters.dateRange) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Selection Required',
                    text: 'Please select a date range first.',
                    confirmButtonColor: '#3085d6'
                });
                return;
            }
            
            this.isButtonDisabled = true;

            // Extract enrolled_from and enrolled_to from date range string
            const dates = this.exportFilters.dateRange.split(' to ');
            const enrolled_from = dates[0];
            const enrolled_to = dates[1] || dates[0];

            const params = {
                enrollment_status: this.status || 'all',
                status: this.mdmStatus,
                os: this.os,
                enrolled_from,
                enrolled_to
            };

            axios.get(process.env.VUE_APP_API_BASEURL + 'mdm/export', { 
                params,
                responseType: 'blob' 
            })
            .then(response => {
                this.isButtonDisabled = false;
                
                // Construct download link
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `MDM_History_Export_${moment().format('DD-MM-YYYY_hhmmA')}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                this.closeExportModal();

                Swal.fire({
                    icon: 'success',
                    title: 'Report Generated',
                    text: 'Your MDM device report has been downloaded.',
                    timer: 2000,
                    showConfirmButton: false
                });
            })
            .catch(error => {
                this.isButtonDisabled = false;
                console.error("Export Error:", error);

                Swal.fire({
                    icon: 'error',
                    title: 'Export Failed',
                    text: 'An error occurred while generating the report. Please try again.',
                    confirmButtonColor: '#f46a6a',
                });
            });
        },

        handleExportHistorical() {
            if (!this.exportFilters.dateRange) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Selection Required',
                    text: 'Please select a date range first.',
                    confirmButtonColor: '#3085d6'
                });
                return;
            }

            this.isButtonDisabled = true;

            // Extract requested_from and requested_to from date range string
            const dates = this.exportFilters.dateRange.split(' to ');
            const requested_from = dates[0];
            const requested_to = dates[1] || dates[0];

            const params = {
                requested_from,
                requested_to
            };

            axios.get(process.env.VUE_APP_API_BASEURL + 'mdm/command-logs/export', { 
                params,
                responseType: 'blob' 
            })
            .then(response => {
                this.isButtonDisabled = false;
                
                // Construct download link
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `MDM_Command_Logs_Export_${moment().format('DD-MM-YYYY_hhmmA')}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                this.closeExportModal();

                Swal.fire({
                    icon: 'success',
                    title: 'History Report Generated',
                    text: 'Your MDM command history report has been downloaded.',
                    timer: 2000,
                    showConfirmButton: false
                });
            })
            .catch(error => {
                this.isButtonDisabled = false;
                console.error("Export Error:", error);

                Swal.fire({
                    icon: 'error',
                    title: 'Export Failed',
                    text: 'An error occurred while generating the history report. Please try again.',
                    confirmButtonColor: '#f46a6a',
                });
            });
        },

        /**
         * Returns a badge class based on MdmState enum:
         *   1 = Active, 3 = Lost Mode
         */
        mdmStateClass(status) {
            const map = {
                1: 'badge-soft-success', // Active
                3: 'badge-soft-danger',  // Lost Mode
            };
            return map[status] ?? 'badge-soft-secondary';
        },

        /**
         * Returns a badge class based on MDM Profile Status:
         *   'enrolled' | 'removed' | 'unknown' | null
         */
        mdmProfileStatusClass(status) {
            const map = {
                'enrolled': 'badge-soft-success',
                'removed': 'badge-soft-danger',
                'unknown': 'badge-soft-secondary',
            };
            return map[status] ?? 'badge-soft-warning';
        },

        /**
         * Returns a label for MDM Profile Status
         */
        mdmProfileStatusLabel(status) {
            const map = {
                'enrolled': 'Enrolled',
                'removed': 'Removed',
                'unknown': 'Unknown',
            };
            return map[status] ?? 'Not Enrolled';
        },

        viewHistory(item) {
            this.selectedDeviceName = item.device_details?.product_name || 'Device';
            this.selectedDevice = item;
            this.showHistoryModal = true;
            this.historyLoading = true;
            this.historyList = [];
            document.body.classList.add('modal-open');
            
            axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt');
            axios.get(process.env.VUE_APP_API_BASEURL + `mdm/${item.id}/history`)
                .then(response => {
                    this.historyLoading = false;
                    if (response && response.data && response.data.data) {
                        this.historyList = response.data.data;
                    }
                })
                .catch(error => {
                    this.historyLoading = false;
                    console.error("Fetch History Error:", error);
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: error.response ? error.response.data.message : error.message,
                        confirmButtonColor: '#f46a6a',
                    });
                });
        },
        closeHistoryModal() {
            this.showHistoryModal = false;
            this.historyList = [];
            this.selectedDevice = null;
            document.body.classList.remove('modal-open');
        },
        historyTypeBadgeClass(type) {
            const map = {
                'warning': 'badge-soft-warning',
                'enable_lost_mode': 'badge-soft-danger',
                'disable_lost_mode': 'badge-soft-info',
                1: 'badge-soft-warning',      // Warning
                2: 'badge-soft-danger',       // Enable Lost Mode
                3: 'badge-soft-info'          // Disable Lost Mode
            };
            return map[type] ?? 'badge-soft-secondary';
        },
        historyStatusBadgeClass(status, statusDesc) {
            const map = {
                'completed': 'badge-soft-success',
                'failed': 'badge-soft-danger',
                'rejected': 'badge-soft-danger',
                'processing': 'badge-soft-warning',
                'disabling_lost_mode': 'badge-soft-warning',
                'waiting': 'badge-soft-warning',
                'lost_mode_enabled': 'badge-soft-purple',
                'approved_queued': 'badge-soft-primary',
                'draft': 'badge-soft-secondary',
                4: 'badge-soft-success',       // Completed
                5: 'badge-soft-danger'         // Failed
            };
            if (map[status]) return map[status];
            
            if (statusDesc) {
                const desc = statusDesc.toLowerCase();
                if (desc.includes('complete') || desc.includes('success')) return 'badge-soft-success';
                if (desc.includes('fail') || desc.includes('reject')) return 'badge-soft-danger';
                if (desc.includes('wait') || desc.includes('process') || desc.includes('queue')) return 'badge-soft-warning';
                if (desc.includes('lost') || desc.includes('lock')) return 'badge-soft-purple';
            }
            return 'badge-soft-secondary';
        },
        mdmPortalUrl(device) {
            if (!device || !device.device_details) return '#';
            const sn = device.device_details.serial_number || '';
            let os = (device.device_details.os_type || '').toLowerCase();
            if (os === 'ios' || os === 'mac' || os === 'macos' || os === 'iphone' || os === 'ipad') {
                os = 'iphone';
            } else if (os === 'android') {
                os = 'android';
            } else {
                os = 'iphone';
            }
            return `https://mdm-portal.compasia.com:4003/devices/${os}/${sn}`;
        },
        isIosDevice(device) {
            if (!device || !device.device_details) return false;
            const os = (device.device_details.os_type || '').toLowerCase();
            return ['ios', 'mac', 'macos', 'iphone', 'ipad'].includes(os);
        },
    },
    mounted() {
        this.getDeviceList();
    }
};
</script>

<template>
    <div class="card">
        <div class="card-header border-bottom-dashed">
            <div class="d-flex align-items-center flex-wrap gap-2">
                <div class="flex-grow-1">
                    <form @submit.prevent="submitFilter" class="row g-1 align-items-center">
                        <div class="col-auto">
                            <select v-model="status" class="form-select form-select-sm" style="width: 180px;">
                                <option value="">Show All Devices</option>
                                <option value="linked">Linked to Contract</option>
                                <option value="not_linked">Not Linked to Contract</option>
                            </select>
                        </div>
                        <div class="col-auto">
                            <select v-model="mdmStatus" class="form-select form-select-sm" style="width: 110px;">
                                <option value="">Status</option>
                                <option value="1">Active</option>
                                <option value="3">Lost Mode</option>
                            </select>
                        </div>
                        <div class="col-auto">
                            <select v-model="os" class="form-select form-select-sm" style="width: 110px;">
                                <option value="">OS</option>
                                <option value="ios">iOS</option>
                                <option value="android">Android</option>
                            </select>
                        </div>
                        <div class="col-auto">
                            <button type="submit" class="btn btn-primary btn-sm" :disabled="isButtonDisabled" title="Apply Filter">
                                <i class="ri-filter-2-line me-1 align-bottom"></i> Filter
                            </button>
                        </div>
                        <div class="col-auto">
                            <button type="button" @click='resetFilter' class="btn btn-light btn-sm" title="Reset Filters">
                                <i class="ri-refresh-line align-bottom"></i>
                            </button>
                        </div>
                    </form>
                </div>
                <div class="flex-shrink-0" v-if="canExport">
                    <button type="button" @click="openExportModal" class="btn btn-soft-success btn-sm px-3">
                        <i class="ri-file-excel-2-line me-1 align-bottom"></i> Export Report
                    </button>
                </div>
            </div>
        </div>
        <div class="card-body">
            <div v-if='loading' class="text-center p-5">
                <div class="spinner-border text-success" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
            </div>
            <div v-else class="table-responsive">
                <DataTable :key="dtKey" :options="options" class="table table-hover table-nowrap align-middle mb-0"> 
                    <thead class="table-light">
                        <tr>
                            <th style="width: 30px;" class="text-center">No.</th>
                            <th>Device Details</th>
                            <th>Customer</th>
                            <th>Contract & Order</th>
                            <th>MDM Details</th>
                            <th>System Logs</th>
                        </tr>
                    </thead>
                    <tbody>                        
                        <tr v-for="(item, index) in deviceList" :key="index">
                            <td class="text-center text-muted">{{ index + 1 }}</td>
                            
                            <!-- Device Details -->
                            <td>
                                <div class="d-flex align-items-center">
                                    <div class="flex-grow-1">
                                        <h6 class="fs-14 mb-1">{{ item.device_details.product_name }}</h6>
                                        <div class="text-muted fs-12">
                                            SN: <span class="text-dark">{{ item.device_details.serial_number }}</span>
                                        </div>
                                        <div class="text-muted fs-12">
                                            IMEI 1: <span class="text-dark">{{ item.device_details.imei_1 }}</span>
                                        </div>
                                        <div class="text-muted fs-12">
                                            IMEI 2: <span class="text-dark">{{ item.device_details.imei_2 || '---' }}</span>
                                        </div>
                                        <div class="text-muted fs-12">
                                            OS: <span class="text-dark text-capitalize">{{ item.device_details.os_type || '---' }}</span> | 
                                            Type: <span class="text-dark text-capitalize">{{ item.device_details.device_type || '---' }}</span>
                                        </div>
                                    </div>
                                </div>
                            </td>

                            <!-- Customer -->
                            <td>
                                <router-link 
                                    :to="`/customer/customer-details/${item.customer.id}`"
                                    class="text-primary fw-medium fs-13"
                                >
                                    {{ item.customer.name }}
                                </router-link>
                                <div class="text-muted fs-12">{{ item.customer.ic || '---' }}</div>
                            </td>

                            <!-- Contract & Order -->
                            <td>
                                <div class="mb-1">
                                    <router-link :to="`/contracts/customer-contract-details/${item.contract.id}`" class="text-primary fw-medium fs-13">
                                        {{ item.contract.number }}
                                    </router-link>
                                </div>
                                <router-link :to="`/orders/customer-order-details/${item.customer?.id}`" class="text-primary fw-medium fs-13">
                                    {{ item.order.number }}
                                </router-link>
                            </td>   

                            <!-- MDM Status & Health -->
                            <td>
                                <div class="d-flex gap-2 mb-2 flex-wrap">
                                    <span :class="['badge', mdmStateClass(item.mdm_details.status)]">
                                        {{ item.mdm_details.status_description }}
                                    </span>
                                    <span :class="['badge', mdmProfileStatusClass(item.mdm_details.mdm_profile_status)]">
                                        {{ mdmProfileStatusLabel(item.mdm_details.mdm_profile_status) }}
                                    </span>
                                </div>
                                <div class="text-muted fs-11">
                                    <span class="fw-medium text-dark">Enrolled:</span> {{ item.mdm_details.enrolled_at || '---' }}
                                </div>

                                <!-- Integrated History Stats (Merged as requested) -->
                                <div class="mt-2 d-flex align-items-center gap-2 border-top pt-1 opacity-75">
                                    <div class="fs-10 text-uppercase fw-bold text-muted">History:</div>
                                    <div class="fs-11">
                                        <span class="text-warning fw-medium">Warn:</span> {{ item.command_statistics?.warning_count || 0 }}
                                    </div>
                                    <div class="vr" style="height: 10px;"></div>
                                    <div class="fs-11">
                                        <span class="text-danger fw-medium">Lost:</span> {{ item.command_statistics?.lost_mode_enabled_count || 0 }}
                                    </div>
                                    <div class="vr" style="height: 10px;"></div>
                                    <div class="fs-11">
                                        <span class="text-info fw-medium">Disable:</span> {{ item.command_statistics?.lost_mode_disabled_count || 0 }}
                                    </div>
                                </div>
                            </td>                                          
                            

                            <!-- Timestamps -->
                            <td>
                                <div class="text-muted fs-11 mb-1">
                                    <i class="ri-add-circle-line align-middle me-1"></i>{{ item.system_logs.created_at }}
                                </div>
                                <div class="text-muted fs-11 mb-2">
                                    <i class="ri-refresh-line align-middle me-1"></i>{{ item.system_logs.updated_at }}
                                </div>
                                <button 
                                    type="button" 
                                    @click="viewHistory(item)" 
                                    data-bs-toggle="modal" 
                                    data-bs-target="#mdmDeviceHistoryModal" 
                                    class="btn btn-soft-primary btn-sm w-100"
                                >
                                    <i class="ri-history-line me-1 align-bottom"></i> History
                                </button>
                            </td>                                         
                        </tr>
                    </tbody>
                </DataTable>
            </div>
        </div>

        <!-- Export History Modal -->
        <div v-if="showExportModal" class="modal-backdrop fade show"></div>
        <div 
            class="modal fade" 
            :class="{ show: showExportModal }" 
            :style="{ display: showExportModal ? 'block' : 'none' }"
            tabindex="-1" 
            role="dialog"
        >
            <div class="modal-dialog modal-dialog-centered" role="document">
                <div class="modal-content border-0 shadow">
                    <div class="modal-header bg-soft-success py-3">
                        <h5 class="modal-title fw-bold text-success">
                            <i class="ri-file-excel-2-line me-2 align-bottom"></i> Export MDM Reports
                        </h5>
                        <button type="button" class="btn-close" @click="closeExportModal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body p-4">
                        <div class="mb-0">
                            <label class="form-label text-muted fs-12 text-uppercase fw-bold">Select Date Range</label>
                            <flat-pickr 
                                v-model="exportFilters.dateRange" 
                                :config="flatpickrConfig" 
                                class="form-control" 
                                placeholder="Select Start & End Date"
                            ></flat-pickr>
                            <div class="mt-3 fs-11 text-muted border-top pt-2">
                                <div class="d-flex align-items-center mb-1">
                                    <i class="ri-device-line me-2 text-success"></i>
                                    <span><strong>Devices Export:</strong> follows enrollment date.</span>
                                </div>
                                <div class="d-flex align-items-center">
                                    <i class="ri-history-line me-2 text-info"></i>
                                    <span><strong>Actions History:</strong> follows requested date (requested_at).</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer bg-light p-3">
                        <button type="button" class="btn btn-ghost-danger btn-sm px-3 mr-auto" @click="closeExportModal">Cancel</button>
                        
                        <button 
                            type="button" 
                            class="btn btn-info btn-sm px-4" 
                            @click="handleExportHistorical"
                            :disabled="isButtonDisabled"
                        >
                            <i class="ri-history-line me-1 align-bottom"></i> Export Actions History
                        </button>
                        
                        <button 
                            type="button" 
                            class="btn btn-success btn-sm px-4" 
                            @click="handleExportDevices"
                            :disabled="isButtonDisabled"
                        >
                            <span v-if="isButtonDisabled">
                                <span class="spinner-border spinner-border-sm me-1" role="status"></span> Generating...
                            </span>
                            <span v-else>
                                <i class="ri-file-download-line me-1 align-bottom"></i> Export Devices
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Device Command History Modal -->
        <div 
            class="modal fade" 
            id="mdmDeviceHistoryModal"
            tabindex="-1" 
            role="dialog"
            aria-hidden="true"
        >
            <div class="modal-dialog modal-dialog-centered modal-xl" role="document">
                <div class="modal-content border-0 shadow">
                    <div class="modal-header bg-soft-primary py-3 d-block">
                        <div class="d-flex align-items-center justify-content-between">
                            <h5 class="modal-title fw-bold text-primary mb-0 d-flex align-items-center gap-2">
                                <i class="ri-history-line align-bottom"></i> 
                                <span>Command History - {{ selectedDeviceName }}</span>
                                <a 
                                    v-if="selectedDevice && isIosDevice(selectedDevice)" 
                                    :href="mdmPortalUrl(selectedDevice)" 
                                    target="_blank" 
                                    class="text-primary fs-16 lh-1" 
                                    title="Open in MDM Portal"
                                >
                                    <i class="ri-external-link-line"></i>
                                </a>
                            </h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal" @click="closeHistoryModal" aria-label="Close"></button>
                        </div>
                        <div v-if="selectedDevice" class="d-flex align-items-center flex-wrap gap-3 mt-2 text-muted fs-12 border-top pt-2">
                            <div>
                                <span class="fw-medium">SN:</span> <span class="text-dark fw-semibold">{{ selectedDevice.device_details?.serial_number || '---' }}</span>
                            </div>
                            <div class="vr" style="height: 12px;"></div>
                            <div>
                                <span class="fw-medium">OS:</span> <span class="text-dark text-capitalize fw-semibold">{{ selectedDevice.device_details?.os_type || '---' }}</span>
                            </div>
                            <div class="vr" style="height: 12px;"></div>
                            <div>
                                <span class="fw-medium">Type:</span> <span class="text-dark text-capitalize fw-semibold">{{ selectedDevice.device_details?.device_type || '---' }}</span>
                            </div>
                            <div class="vr" style="height: 12px;"></div>
                            <div>
                                <span class="fw-medium">MDM Status:</span>
                                <span :class="['badge ms-1', mdmStateClass(selectedDevice.mdm_details?.status)]">
                                    {{ selectedDevice.mdm_details?.status_description || '---' }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="modal-body p-4" style="max-height: 70vh; overflow-y: auto;">
                        <div v-if='historyLoading' class="text-center p-5">
                            <div class="spinner-border text-primary" role="status">
                                <span class="sr-only">Loading...</span>
                            </div>
                        </div>
                        <div v-else-if="historyList.length === 0" class="text-center p-5 text-muted">
                            <i class="ri-inbox-line fs-24 mb-2 d-block"></i>
                            No command history found for this device.
                        </div>
                        <div v-else class="table-responsive">
                            <table class="table table-hover table-nowrap align-middle mb-0">
                                <thead class="table-light">
                                    <tr>
                                        <th style="width: 30px;" class="text-center">No.</th>
                                        <th>Command Type</th>
                                        <th>Status</th>
                                        <th>Batch ID</th>
                                        <th>Requested By</th>
                                        <th>Approved/Rejected By</th>
                                        <th>Timestamps</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(hist, idx) in historyList" :key="hist.id">
                                        <td class="text-center text-muted">{{ idx + 1 }}</td>
                                        <td>
                                            <span :class="['badge', historyTypeBadgeClass(hist.type)]">
                                                {{ hist.type_description }}
                                            </span>
                                        </td>
                                        <td>
                                            <span :class="['badge', historyStatusBadgeClass(hist.status, hist.status_description)]">
                                                {{ hist.status_description }}
                                            </span>
                                        </td>
                                        <td>
                                            <router-link 
                                                v-if="hist.mdm_import_batch_id" 
                                                :to="{ path: '/device/mdm-devices', query: { open_batch_id: hist.mdm_import_batch_id, type: hist.type } }"
                                                target="_blank"
                                                class="text-primary fw-semibold"
                                            >
                                                #{{ hist.mdm_import_batch_id }}
                                            </router-link>
                                            <span v-else class="text-muted">---</span>
                                        </td>
                                        <td>
                                            <div v-if="hist.submission_approval?.requested_by">
                                                <div class="fw-medium text-dark">{{ hist.submission_approval.requested_by }}</div>
                                                <div class="text-muted fs-11">{{ hist.submission_approval.requested_at }}</div>
                                            </div>
                                            <span v-else class="text-muted">---</span>
                                        </td>
                                        <td>
                                            <div v-if="hist.submission_approval?.approved_by">
                                                <div class="fw-medium text-success">{{ hist.submission_approval.approved_by }}</div>
                                                <div class="text-muted fs-11">{{ hist.submission_approval.approved_at }}</div>
                                            </div>
                                            <div v-else-if="hist.submission_approval?.rejected_by">
                                                <div class="fw-medium text-danger">{{ hist.submission_approval.rejected_by }}</div>
                                                <div class="text-muted fs-11">{{ hist.submission_approval.rejected_at }}</div>
                                            </div>
                                            <span v-else class="text-muted">---</span>
                                        </td>
                                        <td>
                                            <div class="text-muted fs-11">
                                                <strong>Created:</strong> {{ format_date(hist.created_at) }}
                                            </div>
                                            <div class="text-muted fs-11">
                                                <strong>Updated:</strong> {{ format_date(hist.updated_at) }}
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div class="modal-footer bg-light p-3">
                        <button type="button" class="btn btn-light btn-sm px-4" data-bs-dismiss="modal" @click="closeHistoryModal">Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.badge-soft-purple {
    color: #8b5cf6;
    background-color: rgba(139, 92, 246, 0.1);
}
</style>
